/**
 * A* Pathfinding for Orthogonal Wire Routing
 *
 * FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/pathfinding.js
 *
 * Implements A* algorithm for finding optimal orthogonal paths around obstacles
 */

/**
 * Priority Queue implementation for A* algorithm
 */
class PriorityQueue {
  constructor() {
    this.items = [];
  }

  enqueue(item, priority) {
    this.items.push({ item, priority });
    this.items.sort((a, b) => a.priority - b.priority);
  }

  dequeue() {
    return this.items.shift()?.item;
  }

  isEmpty() {
    return this.items.length === 0;
  }
}

/**
 * Grid node for A* pathfinding
 */
class GridNode {
  constructor(x, y) {
    this.x = x;
    this.y = y;
    this.g = Infinity; // Cost from start
    this.h = 0; // Heuristic to goal
    this.f = Infinity; // Total cost (g + h)
    this.parent = null;
    this.isObstacle = false;
  }

  get key() {
    return `${this.x},${this.y}`;
  }
}

/**
 * Build obstacle grid from component bounding boxes and wire paths
 */
export function buildObstacleGrid(components, gridSize, canvasWidth, canvasHeight, offsetX = 0, offsetY = 0, existingWirePaths = []) {
  const cols = Math.ceil(canvasWidth / gridSize);
  const rows = Math.ceil(canvasHeight / gridSize);

  // Initialize grid
  const grid = [];
  for (let y = 0; y < rows; y++) {
    grid[y] = [];
    for (let x = 0; x < cols; x++) {
      grid[y][x] = new GridNode(x, y);
    }
  }

  // Mark component obstacles
  let obstacleCount = 0;
  components.forEach((component) => {
    if (!component || !component.getComponent || !component.getPosition) {
      console.warn('[Pathfinding] Invalid component in obstacle grid:', component);
      return;
    }

    const bounds = component.getComponent();
    const pos = component.getPosition();

    // Folders ARE obstacles - revert the skip
    // (Folders are visual containers that should block wire routing)

    if (!pos || pos.x === undefined || pos.y === undefined) {
      console.warn('[Pathfinding] Component has invalid position:', bounds, pos);
      return;
    }

    // Component bounding box (NO padding - exact bounds only)
    const padding = 0; // No padding - use exact component bounds
    const width = bounds.width || 120;
    const height = bounds.height || 100;

    // Apply offset to handle negative coordinates
    // Use FLOOR for both min and max to get exact grid cell coverage
    const minX = Math.floor((pos.x + offsetX) / gridSize);
    const maxX = Math.floor((pos.x + offsetX + width) / gridSize);
    const minY = Math.floor((pos.y + offsetY) / gridSize);
    const maxY = Math.floor((pos.y + offsetY + height) / gridSize);

    const canvasMinX = minX * gridSize - offsetX;
    const canvasMaxX = (maxX + 1) * gridSize - offsetX;
    const canvasMinY = minY * gridSize - offsetY;
    const canvasMaxY = (maxY + 1) * gridSize - offsetY;

    // Mark grid cells as obstacles (inclusive range)
    for (let y = Math.max(0, minY); y <= Math.min(rows - 1, maxY); y++) {
      for (let x = Math.max(0, minX); x <= Math.min(cols - 1, maxX); x++) {
        if (grid[y] && grid[y][x]) {
          grid[y][x].isObstacle = true;
          obstacleCount++;
        }
      }
    }
  });

  // Mark existing wire FIRST AND LAST SEGMENTS as obstacles (prevent routing through near-slot areas)
  // We only mark the segments immediately adjacent to slots to give clearance
  let wireObstacleCount = 0;
  existingWirePaths.forEach((wirePath, wireIndex) => {
    if (!wirePath || wirePath.length < 2) {return;}

    // Mark ONLY the first and last segments (immediately adjacent to slots)
    const segmentsToMark = [
      [0, 1], // First segment (from output slot)
      [wirePath.length - 2, wirePath.length - 1], // Last segment (to input slot)
    ];

    segmentsToMark.forEach(([startIdx, endIdx]) => {
      if (startIdx >= wirePath.length || endIdx >= wirePath.length) {return;}

      const start = wirePath[startIdx];
      const end = wirePath[endIdx];

      if (!start || !end) {return;}

      // Convert to grid coordinates
      const x1 = Math.floor((start.x + offsetX) / gridSize);
      const y1 = Math.floor((start.y + offsetY) / gridSize);
      const x2 = Math.floor((end.x + offsetX) / gridSize);
      const y2 = Math.floor((end.y + offsetY) / gridSize);

      // Mark all cells along the line segment (Bresenham's line algorithm)
      const dx = Math.abs(x2 - x1);
      const dy = Math.abs(y2 - y1);
      const sx = x1 < x2 ? 1 : -1;
      const sy = y1 < y2 ? 1 : -1;
      let err = dx - dy;

      let x = x1;
      let y = y1;

      while (true) {
        // Mark this cell as obstacle
        if (grid[y] && grid[y][x] && !grid[y][x].isObstacle) {
          grid[y][x].isObstacle = true;
          wireObstacleCount++;
        }

        if (x === x2 && y === y2) {break;}

        const e2 = 2 * err;
        if (e2 > -dy) {
          err -= dy;
          x += sx;
        }
        if (e2 < dx) {
          err += dx;
          y += sy;
        }
      }
    });
  });

  return grid;
}

/**
 * Manhattan distance heuristic (perfect for orthogonal routing)
 */
function manhattanDistance(node, goal) {
  return Math.abs(node.x - goal.x) + Math.abs(node.y - goal.y);
}

/**
 * Get neighbors for unblocking (returns nodes even if they're obstacles)
 */
function getNeighborsForUnblocking(x, y, grid) {
  const neighbors = [];
  const directions = [
    { dx: 0, dy: -1 }, // Up
    { dx: 1, dy: 0 },  // Right
    { dx: 0, dy: 1 },  // Down
    { dx: -1, dy: 0 }, // Left
  ];

  directions.forEach(({ dx, dy }) => {
    const newX = x + dx;
    const newY = y + dy;

    if (grid[newY] && grid[newY][newX]) {
      neighbors.push(grid[newY][newX]);
    }
  });

  return neighbors;
}

/**
 * Get neighbors of a grid node (only orthogonal - no diagonals)
 */
function getNeighbors(node, grid) {
  const neighbors = [];
  const directions = [
    { dx: 0, dy: -1 }, // Up
    { dx: 1, dy: 0 },  // Right
    { dx: 0, dy: 1 },  // Down
    { dx: -1, dy: 0 }, // Left
  ];

  directions.forEach(({ dx, dy }) => {
    const newX = node.x + dx;
    const newY = node.y + dy;

    if (grid[newY] && grid[newY][newX] && !grid[newY][newX].isObstacle) {
      neighbors.push(grid[newY][newX]);
    }
  });

  return neighbors;
}

/**
 * Calculate movement cost with direction change penalty
 */
function getMovementCost(current, neighbor, parent) {
  let cost = 1; // Base movement cost

  // Add small penalty for direction changes (prefer straight runs but not at the expense of distance)
  if (parent) {
    const prevDx = current.x - parent.x;
    const prevDy = current.y - parent.y;
    const nextDx = neighbor.x - current.x;
    const nextDy = neighbor.y - current.y;

    // If direction changed, add small penalty
    // Keep this low so A* prefers shorter paths over fewer turns
    if (prevDx !== nextDx || prevDy !== nextDy) {
      cost += 0.3; // Small turn penalty - total distance is more important
    }
  }

  return cost;
}

/**
 * Find path using Lee's algorithm (maze routing)
 * More reliable than A* for orthogonal routing with obstacles
 */
export function findPath(startPoint, endPoint, grid, gridSize) {
  if (!grid || !grid.length) {return null;}

  // Convert canvas coordinates to grid coordinates
  const startX = Math.floor(startPoint.x / gridSize);
  const startY = Math.floor(startPoint.y / gridSize);
  const endX = Math.floor(endPoint.x / gridSize);
  const endY = Math.floor(endPoint.y / gridSize);

  // Validate coordinates
  if (!grid[startY] || !grid[startY][startX] || !grid[endY] || !grid[endY][endX]) {
    console.warn('[Pathfinding] Start or end point outside grid bounds');
    return null;
  }

  const startNode = grid[startY][startX];
  const endNode = grid[endY][endX];

  // Reset grid
  grid.forEach((row) => {
    row.forEach((node) => {
      node.distance = Infinity;
      node.parent = null;
    });
  });

  // Check if start/end are blocked
  if (startNode.isObstacle) {
    console.error('[Pathfinding] Start node is blocked!', {
      startGrid: `(${startX},${startY})`,
      startCanvas: `(${startPoint.x}, ${startPoint.y})`,
    });
    return null;
  }
  if (endNode.isObstacle) {
    console.error('[Pathfinding] End node is blocked!', {
      endGrid: `(${endX},${endY})`,
      endCanvas: `(${endPoint.x}, ${endPoint.y})`,
    });
    return null;
  }

  // Lee's algorithm: BFS wave expansion
  const queue = [];
  startNode.distance = 0;
  queue.push(startNode);

  let iterations = 0;
  const maxIterations = 50000;

  while (queue.length > 0 && iterations < maxIterations) {
    iterations++;
    const current = queue.shift();

    // Found the goal
    if (current === endNode) {
      return reconstructPath(current);
    }

    // Explore neighbors
    const neighbors = getNeighbors(current, grid);

    neighbors.forEach((neighbor) => {
      if (neighbor.distance === Infinity) {
        neighbor.distance = current.distance + 1;
        neighbor.parent = current;
        queue.push(neighbor);
      }
    });
  }

  // No path found
  console.warn('[Pathfinding] No path found', {
    iterations,
    reason: iterations >= maxIterations ? 'max iterations' : 'queue empty',
  });
  return null;
}

/**
 * Reconstruct path from end node to start
 */
function reconstructPath(endNode) {
  const path = [];
  let current = endNode;

  while (current) {
    path.unshift({ x: current.x, y: current.y });
    current = current.parent;
  }

  return path;
}

/**
 * Smooth path by removing redundant waypoints
 * (Remove middle point if 3 consecutive points are collinear)
 */
export function smoothPath(gridPath) {
  if (!gridPath || gridPath.length <= 2) {return gridPath;}

  const smoothed = [gridPath[0]];

  for (let i = 1; i < gridPath.length - 1; i++) {
    const prev = gridPath[i - 1];
    const current = gridPath[i];
    const next = gridPath[i + 1];

    // Check if current point is necessary (not collinear)
    const dx1 = current.x - prev.x;
    const dy1 = current.y - prev.y;
    const dx2 = next.x - current.x;
    const dy2 = next.y - current.y;

    // If direction changed, keep the point (it's a corner)
    if (dx1 !== dx2 || dy1 !== dy2) {
      smoothed.push(current);
    }
    // Otherwise skip it (collinear, redundant)
  }

  smoothed.push(gridPath[gridPath.length - 1]);

  return smoothed;
}

/**
 * Convert grid coordinates to canvas coordinates
 */
export function gridToCanvasCoords(gridPath, gridSize) {
  if (!gridPath) {return [];}

  return gridPath.map((point) => ({
    x: point.x * gridSize + gridSize / 2, // Center of grid cell
    y: point.y * gridSize + gridSize / 2,
  }));
}

export default {
  buildObstacleGrid,
  findPath,
  smoothPath,
  gridToCanvasCoords,
};
